import type { CogoBlockDef, CogoElement } from "./types"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RegistryCache = Map<string, { template: any }>

/**
 * Expand $ref/definitions, normalize content→text, and repeat→iterateOver
 * in a loaded block definition before rendering.
 *
 * This handles three patterns used by 21st.dev blocks:
 * 1. definitions + $ref — reusable component templates with #{props.xxx} substitution
 * 2. content → text — 21st.dev uses "content" instead of "text"
 * 3. repeat → iterateOver/itemVar — alternate iteration syntax
 */
export function expandDefinitions(block: CogoBlockDef, registryCache?: RegistryCache): CogoBlockDef {
  const raw = block as unknown as AnyRecord
  const defs: AnyRecord = raw.definitions ?? {}
  if (!raw.definitions && !needsNormalization(block.root)) return block

  block.root = expandElement(block.root, defs, registryCache)
  return block
}

/**
 * Expand $ref references in an element tree.
 * Supports both `#/definitions/xxx` (inline) and `registry:namespace/name` (registry) refs.
 *
 * @param root - The root element to expand
 * @param definitions - Inline definitions map (for #/definitions/ refs)
 * @param registryCache - Optional registry cache (for registry: refs)
 */
export function expandRefs(
  root: CogoElement,
  definitions: AnyRecord = {},
  registryCache?: RegistryCache,
): CogoElement {
  return expandElement(
    JSON.parse(JSON.stringify(root)),
    definitions,
    registryCache,
  )
}

function needsNormalization(el: CogoElement): boolean {
  const raw = el as unknown as AnyRecord
  if (raw.content && !raw.text) return true
  if (raw.repeat) return true
  if (raw.items && raw.itemTemplate) return true
  if (el.children?.some(needsNormalization)) return true
  return false
}

function expandElement(
  el: CogoElement,
  defs: AnyRecord,
  registryCache?: RegistryCache,
): CogoElement {
  const raw = el as unknown as AnyRecord

  // --- Fix 2: content → text ---
  if (raw.content && !raw.text) {
    el.text = raw.content as string
  }

  // --- Fix 6: repeat → iterateOver/itemVar ---
  const repeat = raw.repeat as
    | { over?: string; as?: string }
    | undefined
  if (repeat) {
    if (repeat.over && !el.iterateOver) el.iterateOver = repeat.over
    if (repeat.as && !el.itemVar) el.itemVar = repeat.as
    delete raw.repeat
  }

  // --- Fix 7: Expand $ref:definitions/xxx in options.itemRef ---
  // List elements use options.itemRef = "$ref:definitions/feature-item"
  // to reference a definition template for iterating items.
  const opts = raw.options as AnyRecord | undefined
  if (opts?.itemRef && typeof opts.itemRef === "string") {
    const refStr = opts.itemRef as string
    // Handle both formats: "$ref:definitions/xxx" and "#/definitions/xxx"
    const refMatch = /^\$ref:definitions\/(.+)$/.exec(refStr) ??
                     /^#\/definitions\/(.+)$/.exec(refStr)
    if (refMatch && defs[refMatch[1]]) {
      const defTemplate = defs[refMatch[1]] as AnyRecord
      const cloned = deepCloneWithProps(defTemplate, {}) as unknown as CogoElement
      opts.itemRef = cloned
    }
  }

  // --- Fix 8: items + itemTemplate.$ref → iterateOver + children ---
  // Pattern: container with "items": "#{...}", "itemTemplate": { "$ref": "#/definitions/xxx" }
  // Convert to: iterateOver + itemVar + children with the expanded definition
  const rawItems = raw.items as string | undefined
  const rawItemTemplate = raw.itemTemplate as AnyRecord | undefined
  if (rawItems && rawItemTemplate) {
    const itemRef = rawItemTemplate.$ref as string | undefined
    if (itemRef && itemRef.startsWith("#/definitions/")) {
      const defName = itemRef.replace("#/definitions/", "")
      const defTemplate = defs[defName] as AnyRecord | undefined
      if (defTemplate) {
        const cloned = deepCloneWithProps(defTemplate, {}) as unknown as CogoElement
        // Set iteration properties
        el.iterateOver = rawItems
        el.itemVar = "item"
        el.children = [cloned]
        delete raw.items
        delete raw.itemTemplate
      }
    }
  }

  // --- Fix 1: $ref expansion ---
  if (el.children) {
    el.children = el.children.flatMap((child) =>
      expandChild(child, defs, el.id, registryCache),
    )
  }

  return el
}

function expandChild(
  child: CogoElement,
  defs: AnyRecord,
  parentId: string,
  registryCache?: RegistryCache,
): CogoElement[] {
  const raw = child as unknown as AnyRecord
  const ref = raw.$ref as string | undefined

  // --- Registry $ref resolution ---
  if (ref && ref.startsWith("registry:")) {
    const entry = registryCache?.get(ref)
    if (entry) {
      const props = (raw.props ?? {}) as Record<string, unknown>
      const cloned = deepCloneWithProps(entry.template, props) as unknown as CogoElement
      return [expandElement(cloned, defs, registryCache)]
    }
    // Unknown registry ref — pass through unchanged
    return [child]
  }

  if (ref && ref.startsWith("#/definitions/")) {
    const defName = ref.replace("#/definitions/", "")
    const defTemplate = defs[defName] as AnyRecord | undefined
    if (!defTemplate) return [expandElement(child, defs, registryCache)]

    // Clone definition and substitute props
    const props = (raw.props ?? {}) as Record<string, unknown>
    const cloned = deepCloneWithProps(defTemplate, props) as AnyRecord

    // Use the ref element's id as prefix for uniqueness
    const refId = child.id || `${parentId}_${defName}`
    const expanded = cloned as unknown as CogoElement

    // Set id: if the cloned def has no id, use the ref element's id
    if (!expanded.id) {
      expanded.id = refId
    }

    // Prefix child IDs for uniqueness
    prefixChildIds(expanded, refId)

    // Carry over any extra properties from the ref element (e.g., className, colSpan)
    for (const key of Object.keys(raw)) {
      if (key === "$ref" || key === "props" || key === "id") continue
      if (!(key in cloned)) {
        ;(expanded as unknown as AnyRecord)[key] = raw[key]
      }
    }

    // Recursively expand the cloned subtree
    return [expandElement(expanded, defs, registryCache)]
  }

  return [expandElement(child, defs, registryCache)]
}

function deepCloneWithProps(
  obj: unknown,
  props: Record<string, unknown>,
): unknown {
  if (typeof obj === "string") {
    // Full match: entire string is #{props.xxx} — return raw value (preserves arrays/objects)
    const fullMatch = /^#{props\.(\w+)}$/.exec(obj)
    if (fullMatch) {
      const val = props[fullMatch[1]]
      if (val == null) return ""
      if (typeof val === "object") return deepCloneWithProps(val, props)
      return String(val)
    }
    // Partial match: "text #{props.xxx} more" — string replacement
    return obj.replace(/#{props\.(\w+)}/g, (_, key: string) => {
      const val = props[key]
      return val != null ? String(val) : ""
    })
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => deepCloneWithProps(item, props))
  }
  if (obj && typeof obj === "object") {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj)) {
      result[key] = deepCloneWithProps(value, props)
    }
    return result
  }
  return obj
}

function prefixChildIds(el: CogoElement, prefix: string): void {
  if (el.children) {
    for (const child of el.children) {
      if (child.id && child.id.startsWith("_")) {
        child.id = `${prefix}${child.id}`
      }
      prefixChildIds(child, prefix)
    }
  }
}
